import {
  type CSSProperties,
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { type CoinQr, type ReglageQr, reglagesOverlaySchema } from "../contract";
import { useFlux } from "../direct/use-flux";
import { lireCle } from "./cle";
import { obsDeLaPage } from "./obs";
import {
  DELAI_IMAGE_MS,
  DELAI_LECTURE_MS,
  DELAI_RELECTURE_MS,
  MARGE_PX,
  NOM_TAVERNE,
  POLICE_LIGNE,
  QR_ALT,
  QR_COTE_PX,
  QR_IMAGE,
  QR_LIGNE,
} from "./taverne-texts";

/**
 * L'overlay `taverne` (`/overlay/taverne#cle=...`) : le QR code du stream, dans un coin de l'ecran, avec une ligne
 * dessous. C'est ce qui mene un viewer a `/rejoindre` (ticket 3 du TCG). La regie choisit le coin et l'allume ou
 * l'eteint (`/regie/reglages/overlays`) ; il part cache.
 *
 * - **Rien d'autre a l'ecran que le QR code et sa ligne**, jamais un texte d'erreur : la route porte
 *   `errorComponent: () => null`, et la panne se lit dans la regie, qui liste les overlays connectes. Sans cle, hors
 *   d'OBS, cle refusee, reglages pas encore lus : la page est vide. **Un QR code inconnu ne se montre pas.**
 * - **Il ouvre le flux overlay sous le nom `taverne`** (`Authorization: Cle <cle>`, `cle.ts`), ce qui l'inscrit parmi
 *   les overlays connectes. Le flux porte le message `qr`, qui dit l'etat entier (coin et visibilite) : l'ecran suit
 *   la regie sans rien relire.
 * - **Il lit les reglages** (`GET /api/overlay/reglages`) a l'ouverture du flux, a chaque reconnexion et a chaque
 *   `reprise` : un `qr` a pu lui echapper, et le serveur a pu redemarrer. Une lecture qui echoue est refaite apres
 *   `DELAI_RELECTURE_MS`, et l'ecran ne bouge pas en attendant. Une cle refusee (401), par la lecture ou par le flux
 *   (la regie l'a regeneree depuis), le cache, sans reessayer : la regie ne pourrait plus l'eteindre.
 * - **Une lecture ne contredit jamais un `qr` plus recent** : partie avant lui, elle rend un etat plus vieux que lui.
 * - **Pas d'animation** : le code doit rester lisible. Le panneau apparait et disparait d'un coup, et la police et
 *   l'image sont chargees a l'avance pour qu'il apparaisse entier.
 * - **Hors d'OBS** (`window.obsstudio` absent) ou sans cle, il n'ouvre rien : il ne s'inscrirait pas parmi les overlays
 *   connectes sans etre dans OBS, comme `scene`.
 */

/** Les deux bords que le panneau touche, a `MARGE_PX` de chacun. */
const POSITIONS: Readonly<Record<CoinQr, CSSProperties>> = {
  "haut-gauche": { top: MARGE_PX, left: MARGE_PX },
  "haut-droit": { top: MARGE_PX, right: MARGE_PX },
  "bas-gauche": { bottom: MARGE_PX, left: MARGE_PX },
  "bas-droit": { bottom: MARGE_PX, right: MARGE_PX },
};

/** Ce qu'une lecture des reglages a donne : l'etat, un refus de la cle, ou une panne (reseau, 5xx, forme inattendue). */
type Lecture = { genre: "lu"; qr: ReglageQr } | { genre: "refus" } | { genre: "panne" };

async function lireLesReglages(cle: string): Promise<Lecture> {
  try {
    const reponse = await fetch("/api/overlay/reglages", {
      headers: { authorization: `Cle ${cle}` },
      // Un serveur qui ne repond pas ne doit pas bloquer les lectures suivantes.
      signal: AbortSignal.timeout(DELAI_LECTURE_MS),
    });
    if (reponse.status === 401) return { genre: "refus" };
    if (!reponse.ok) return { genre: "panne" };
    const lu = reglagesOverlaySchema.safeParse(await reponse.json());
    return lu.success ? { genre: "lu", qr: lu.data.qr } : { genre: "panne" };
  } catch {
    // La page ne dit rien de sa panne : la lecture sera refaite.
    return { genre: "panne" };
  }
}

const attendre = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Le panneau : le code et sa ligne. **Il ne se pose qu'une fois l'image arrivee** (`onLoad`) : un SVG qui n'arrive pas
 * (un 404, un deploiement en cours) laisserait sur le stream un bloc blanc, l'icone d'image cassee et le texte `alt`.
 * Tant que l'image n'est pas la, le panneau est en `display: none` (l'image se charge quand meme) et ne montre rien.
 * Une image en erreur est redemandee toutes les `DELAI_IMAGE_MS`, par un nouvel element, jusqu'a ce qu'elle arrive. Un
 * panneau qui se retire puis revient repart de zero : il attend la sienne, et n'affiche jamais celle d'une fois d'avant.
 */
function PanneauQr({ coin }: { coin: CoinQr }): ReactElement {
  const [chargee, setChargee] = useState(false);
  const [essai, setEssai] = useState(0);
  const minuteur = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(minuteur.current), []);

  return (
    <div
      data-testid="qr-taverne"
      data-coin={coin}
      style={{ position: "fixed", ...POSITIONS[coin], ...(chargee ? {} : { display: "none" }) }}
      className="rounded-[10px] border-2 border-primary bg-background/90 p-3 shadow-[0_0_0_2px_var(--background)]"
    >
      {/* Le panneau a la largeur du code, pas celle de sa ligne : le code tombe sur des pixels entiers, quelle que soit la police. */}
      <div
        data-testid="qr-taverne-colonne"
        style={{ width: QR_COTE_PX }}
        className="flex flex-col items-center gap-3"
      >
        <img
          key={essai}
          src={QR_IMAGE}
          alt={QR_ALT}
          width={QR_COTE_PX}
          height={QR_COTE_PX}
          style={{ width: QR_COTE_PX, height: QR_COTE_PX }}
          className="block bg-white"
          onLoad={() => setChargee(true)}
          onError={() => {
            clearTimeout(minuteur.current);
            minuteur.current = setTimeout(() => setEssai((n) => n + 1), DELAI_IMAGE_MS);
          }}
        />
        <p className="police-titre m-0 whitespace-nowrap text-[26px] leading-[30px] text-[#FCDD8E] [text-shadow:2px_2px_0_#4A2C63]">
          {QR_LIGNE}
        </p>
      </div>
    </div>
  );
}

export function TaverneOverlay(): ReactElement | null {
  // La cle est lue une fois, au premier rendu : elle quitte alors l'adresse.
  const [cle] = useState(() => lireCle());
  const [obs] = useState(() => obsDeLaPage());
  const pret = cle !== null && obs !== undefined;
  const [reglage, setReglage] = useState<ReglageQr | null>(null);

  /**
   * Ce que la page sait de ses lectures : la derniere lancee (une plus vieille ne compte plus), combien de `qr` le
   * flux a portes, et si la page est encore la.
   */
  const suivi = useRef({ lecture: 0, messages: 0, actif: true });

  const lire = useCallback((): void => {
    if (cle === null) return;
    const s = suivi.current;
    s.lecture += 1;
    const numero = s.lecture;
    const messagesAvant = s.messages;
    void (async () => {
      for (;;) {
        const lecture = await lireLesReglages(cle);
        // La page est partie, ou une lecture plus recente a pris le relais.
        if (!s.actif || numero !== s.lecture) return;
        if (lecture.genre === "refus") {
          setReglage(null);
          return;
        }
        if (lecture.genre === "lu") {
          // Un `qr` arrive pendant la lecture dit un etat plus recent que celui qu'elle rend.
          if (messagesAvant === s.messages) setReglage(lecture.qr);
          return;
        }
        await attendre(DELAI_RELECTURE_MS);
        if (!s.actif || numero !== s.lecture) return;
      }
    })();
  }, [cle]);

  useFlux(
    pret ? `/api/overlay/flux?nom=${NOM_TAVERNE}` : null,
    cle === null ? undefined : { authorization: `Cle ${cle}` },
    {
      onMessage: (message) => {
        if (message.genre !== "qr") return;
        suivi.current.messages += 1;
        setReglage({ coin: message.coin, visible: message.visible });
      },
      onEtat: (etat) => {
        // L'ouverture du flux, puis chacune de ses reconnexions.
        if (etat.genre === "connecte") lire();
        // Le serveur refuse la cle (regeneree depuis l'ouverture, ou fausse) : la page ne peut plus savoir si le QR
        // code doit rester, et la regie ne peut plus l'eteindre. Il se cache, et aucune lecture en cours ne le remontre.
        if (etat.genre === "coupe" && etat.raison === "refus") {
          suivi.current.lecture += 1;
          setReglage(null);
        }
      },
      onRelire: lire,
    },
  );

  useEffect(() => {
    const s = suivi.current;
    s.actif = true;
    return () => {
      s.actif = false;
    };
  }, []);

  useEffect(() => {
    if (!pret) return;
    // Le panneau apparait entier : sans cela, la premiere image montrerait une police de repli et un carre vide.
    void Promise.resolve(document.fonts?.load(POLICE_LIGNE, QR_LIGNE)).catch(() => undefined);
    new Image().src = QR_IMAGE;
  }, [pret]);

  if (!pret || reglage === null || !reglage.visible) return null;
  return <PanneauQr coin={reglage.coin} />;
}
